import React from 'react'
import { WarningTwoTone } from '@ant-design/icons'
import { Checkbox, Tooltip } from 'antd'
import {
  find,
  get,
  includes,
  isEmpty,
  isNil,
  join,
  map,
  some,
  toLower,
  values,
} from 'lodash'

import { areSameValue } from '../../core/utils'

import {
  JsonDescriptorCollapse,
  ReadMore,
  TextDescriptor,
} from '../../common/components/sections'
import { StyledAiHighlightedText } from '../../common/components/styled'
import {
  AI_HIGHLIGHT_STYLES,
  CERTAINTY_TYPE,
  CURATION_CERTAINTY_THRESHOLD,
} from '../../common/constants'

import { MENU_TITLE_FIELD_NAMES } from '../../menu-title/constants'
import DishActionsCell from '../components/sections/dish-actions-cell'
import DishEditableHighlightingTextFieldCell from '../components/sections/dish-editable-highlighting-text-field-cell'
import DishEditableIngredientsCell from '../components/sections/dish-editable-ingredients-cell'
import DishEditablePriceCell from '../components/sections/dish-editable-price-cell'
import DishEditableSelectCell from '../components/sections/dish-editable-select-cell'
import DishEditableTreeSelectCell from '../components/sections/dish-editable-tree-select-cell'
import DishIdAndErrorCell from '../components/sections/dish-id-and-error-cell'
import DishMenuTitleDetailsCell from '../components/sections/dish-menu-title-details-cell'
import DishReadOnlyIngredientsCell from '../components/sections/dish-read-only-ingredients-cell'
import {
  DIET_ABBREVIATIONS,
  DISH_FIELDS_CONFIG,
  TIER_FILTER,
} from '../constants'
import TIER_ONE_RESOLUTION_TYPE from '../constants/tier-one-resolution-type'
import { computeDishCellClassName } from '../helpers'

const createDishTableCellContent = ({
  dish,
  index,
  field,
  isDisabled,
  isFieldEditable,
  allergenOptions,
  dietOptions,
  treeTypeOptions,
  ingredientOptions,
  isMenuFake,
}) => {
  const className = computeDishCellClassName(field, isFieldEditable, dish)

  if (
    includes(
      [
        DISH_FIELDS_CONFIG.NAME.value,
        DISH_FIELDS_CONFIG.DESCRIPTION.value,
        DISH_FIELDS_CONFIG.INGREDIENTS.value,
      ],
      field,
    )
  ) {
    return {
      props: {
        className,
      },
      children: (
        <DishEditableHighlightingTextFieldCell
          fieldName={field}
          dish={dish}
          isDisabled={isDisabled}
          isTextEditingEnabled={isMenuFake}
          shouldIncreaseMarginOnEdit={index === 0}
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.CERTAINTY.value) {
    let certaintyType = CERTAINTY_TYPE.NO_CURATION_DATA

    if (!isNil(dish.certainty)) {
      certaintyType =
        dish.certainty < CURATION_CERTAINTY_THRESHOLD
          ? CERTAINTY_TYPE.LOW
          : CERTAINTY_TYPE.HIGH
    }

    return {
      props: {
        className,
      },
      children: certaintyType,
    }
  }

  if (field === DISH_FIELDS_CONFIG.TIER.value) {
    const tierLabel = find(values(TIER_FILTER), tierOption =>
      areSameValue(tierOption.value, dish.tier),
    )?.label

    const shouldShowLLMWarning =
      dish?.isTierOneDone &&
      areSameValue(dish?.tierOneResolutionType, TIER_ONE_RESOLUTION_TYPE.LLM)

    return {
      props: {
        className,
      },
      children: shouldShowLLMWarning ? (
        <Tooltip title="Tier one dish solved by LLM">
          <span>
            {tierLabel} <WarningTwoTone twoToneColor="#faad14" />
          </span>
        </Tooltip>
      ) : (
        tierLabel
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.DIETS.value) {
    return {
      props: {
        className,
      },
      children: (
        <DishEditableSelectCell
          dish={dish}
          fieldName={field}
          isDisabled={isDisabled}
          value={map(dish.diets, 'id')}
          options={dietOptions}
          defaultComponent={join(
            map(dish.diets, ({ name }) =>
              get(DIET_ABBREVIATIONS, toLower(name), name),
            ),
            ', ',
          )}
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.ALLERGENS.value) {
    return {
      props: {
        className,
      },
      children: (
        <DishEditableSelectCell
          dish={dish}
          isDisabled={isDisabled}
          fieldName={field}
          value={map(dish.allergens, 'id')}
          options={allergenOptions}
          defaultComponent={join(map(dish.allergens, 'name'), ', ')}
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.PRICE.value) {
    return {
      props: {
        className,
      },
      children: <DishEditablePriceCell isDisabled={isDisabled} dish={dish} />,
    }
  }

  if (field === DISH_FIELDS_CONFIG.MENU_TITLE.value) {
    return {
      props: {
        className,
      },
      children: (
        <DishMenuTitleDetailsCell
          menuTitles={dish.menuTitleParents}
          highlights={dish.highlights}
        />
      ),
    }
  }

  if (
    includes(
      [
        DISH_FIELDS_CONFIG.DISH_TYPE.value,
        DISH_FIELDS_CONFIG.COURSE_TYPE.value,
        DISH_FIELDS_CONFIG.CUISINE_TYPE.value,
      ],
      field,
    )
  ) {
    return {
      props: {
        className,
      },
      children: (
        <DishEditableTreeSelectCell
          fieldName={field}
          dish={dish}
          isDisabled={
            isDisabled || field === DISH_FIELDS_CONFIG.CUISINE_TYPE.value
          }
          value={dish[field]?.id}
          treeData={treeTypeOptions[field]}
          defaultComponent={
            <StyledAiHighlightedText
              highlights={[
                ...(some(dish.aiSuggestion?.[field]?.suggestion, suggestion =>
                  areSameValue(suggestion.id, dish[field]?.id),
                )
                  ? [AI_HIGHLIGHT_STYLES.defaultBlue.value]
                  : []),
              ]}
            >
              {dish[field]?.name}
            </StyledAiHighlightedText>
          }
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.MAIN_INGREDIENTS.value) {
    return {
      props: {
        className,
      },
      children: (
        <DishEditableIngredientsCell
          dish={dish}
          options={ingredientOptions}
          isDisabled={isDisabled}
          fieldName={field}
          ingredientsGroupedByFieldName={{
            [DISH_FIELDS_CONFIG.MAIN_INGREDIENTS.value]:
              dish[DISH_FIELDS_CONFIG.MAIN_INGREDIENTS.value],
            [DISH_FIELDS_CONFIG.ADDITIONAL_INGREDIENTS.value]:
              dish[DISH_FIELDS_CONFIG.ADDITIONAL_INGREDIENTS.value],
          }}
          defaultComponent={
            <DishReadOnlyIngredientsCell
              ingredientsGroupedByFieldName={{
                [DISH_FIELDS_CONFIG.MAIN_INGREDIENTS.value]:
                  dish[DISH_FIELDS_CONFIG.MAIN_INGREDIENTS.value],
                [DISH_FIELDS_CONFIG.ADDITIONAL_INGREDIENTS.value]:
                  dish[DISH_FIELDS_CONFIG.ADDITIONAL_INGREDIENTS.value],
              }}
            />
          }
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.CHOICE_INGREDIENTS.value) {
    return {
      props: {
        className,
      },
      children: (
        <DishEditableIngredientsCell
          dish={dish}
          options={ingredientOptions}
          isDisabled={isDisabled}
          fieldName={field}
          ingredientsGroupedByFieldName={{
            [DISH_FIELDS_CONFIG.CHOICE_INGREDIENTS.value]:
              dish[DISH_FIELDS_CONFIG.CHOICE_INGREDIENTS.value],
          }}
          defaultComponent={
            <DishReadOnlyIngredientsCell
              ingredientsGroupedByFieldName={{
                [DISH_FIELDS_CONFIG.CHOICE_INGREDIENTS.value]:
                  dish[DISH_FIELDS_CONFIG.CHOICE_INGREDIENTS.value],
              }}
            />
          }
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.MENU_TITLE_ADDON_DESCRIPTORS.value) {
    return {
      props: {
        className,
      },
      children: (
        <DishMenuTitleDetailsCell
          menuTitles={dish.menuTitleParents}
          highlights={dish.highlights}
          menuTitleFieldName={MENU_TITLE_FIELD_NAMES.ADDON_DESCRIPTORS}
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.MENU_TITLE_ALLERGEN_DESCRIPTORS.value) {
    return {
      props: {
        className,
      },
      children: (
        <DishMenuTitleDetailsCell
          menuTitles={dish.menuTitleParents}
          highlights={dish.highlights}
          menuTitleFieldName={MENU_TITLE_FIELD_NAMES.ALLERGEN_DESCRIPTORS}
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.MENU_TITLE_DIET_DESCRIPTORS.value) {
    return {
      props: {
        className,
      },
      children: (
        <DishMenuTitleDetailsCell
          menuTitles={dish.menuTitleParents}
          highlights={dish.highlights}
          menuTitleFieldName={MENU_TITLE_FIELD_NAMES.DIET_DESCRIPTORS}
        />
      ),
    }
  }

  if (
    field === DISH_FIELDS_CONFIG.DIET_DESCRIPTORS.value &&
    !isEmpty(dish.dietDescriptors)
  ) {
    return {
      props: {
        className,
      },
      children: (
        <JsonDescriptorCollapse
          value={dish.dietDescriptors}
          highlights={dish.highlights}
          prefix={field}
        />
      ),
    }
  }

  if (
    field === DISH_FIELDS_CONFIG.ALLERGEN_DESCRIPTORS.value &&
    !isEmpty(dish.allergenDescriptors)
  ) {
    return {
      props: {
        className,
      },
      children: (
        <JsonDescriptorCollapse
          value={dish.allergenDescriptors}
          highlights={dish.highlights}
          prefix={field}
        />
      ),
    }
  }

  if (
    field === DISH_FIELDS_CONFIG.ADDON_DESCRIPTORS.value &&
    !isEmpty(dish.addonDescriptors)
  ) {
    return {
      props: {
        className,
      },
      children: (
        <ReadMore
          maxHeight={170}
          component={
            <TextDescriptor
              value={dish.addonDescriptors}
              highlights={dish.highlights}
              prefix={field}
            />
          }
        />
      ),
    }
  }

  if (field === DISH_FIELDS_CONFIG.DISH_ACTIONS.value) {
    return {
      props: {
        className,
      },
      children: <DishActionsCell dish={dish} isDisabled={isDisabled} />,
    }
  }

  if (field === DISH_FIELDS_CONFIG.IS_PUBLISHED.value) {
    return {
      props: {
        className,
      },
      children: <Checkbox checked={dish.isPublished} disabled />,
    }
  }

  if (field === DISH_FIELDS_CONFIG.ID.value) {
    return {
      props: {
        className,
      },
      children: <DishIdAndErrorCell dish={dish} />,
    }
  }

  if (field === DISH_FIELDS_CONFIG.AUTOEAT_ID.value) {
    return {
      props: {
        className,
      },
      children: dish.autoeatId,
    }
  }

  return null
}

export default createDishTableCellContent
